/**
 * OnboardingHeader - Header section for OnboardingProgress banner
 * Displays title, completion count, minimize/close controls
 * @atomic @idempotent
 */
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

const OnboardingHeader = memo(function OnboardingHeader({
  completedCount,
  totalSteps,
  minimized,
  onToggleMinimize,
  onClose
}) {
  const { t } = useTranslation()

  return (
    <div className="flex items-center justify-between px-4 py-3">
      {/* Clickable toggle area — title only */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggleMinimize}
        onTouchEnd={(e) => { e.preventDefault(); onToggleMinimize() }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleMinimize() } }}
        aria-expanded={!minimized}
        className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer hover:opacity-80 active:opacity-60 transition-opacity select-none"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        {/* Progress counter badge */}
        <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-[9px] font-bold leading-none pointer-events-none">
          {completedCount}/{totalSteps}
        </span>
        <h3 className="text-sm font-bold text-gray-900 truncate pointer-events-none">
          {t('onboarding.welcome.progress.title')}
        </h3>
      </div>

      {/* Right controls: chevron toggle + close */}
      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
        <button
          type="button"
          onClick={onToggleMinimize}
          onTouchEnd={(e) => { e.preventDefault(); onToggleMinimize() }}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors focus:outline-none"
          aria-label={minimized ? t('onboarding.welcome.progress.restore') : t('onboarding.welcome.progress.minimize')}
        >
          {minimized ? (
            <ChevronDownIcon className="h-4 w-4 pointer-events-none" />
          ) : (
            <ChevronUpIcon className="h-4 w-4 pointer-events-none" />
          )}
        </button>

        {/* Close button — standalone, doesn't toggle */}
        <button
          type="button"
          onClick={onClose}
          onTouchEnd={(e) => { e.preventDefault(); onClose() }}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors focus:outline-none"
          aria-label={t('common.close')}
        >
          <XMarkIcon className="h-4 w-4 pointer-events-none" />
        </button>
      </div>
    </div>
  )
})

export default OnboardingHeader
